import React, { FC, Fragment, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { helpArticleTemplate } from 'apollo/templates/zendesk';
import { HelpArticle } from 'models/zenddesk';
import { Alert, LinearProgress, Typography } from '@mui/material';
import { classifyHelpLink, openHelpArticleAttachment } from './help-article-links';

type HelpArticleProps = {
    articleId: string;
    onLinkClick: (articleId: string) => void;
};

/**
 * DD-3785: a failed article load or file download is shown here, inside the drawer. It
 * used to go to the error boundary, which replaced the whole app with "Något gick fel".
 */
const HelpArticleView: FC<HelpArticleProps> = ({ articleId, onLinkClick }: HelpArticleProps) => {
    const { t: translate } = useTranslation(['help-drawer']);
    const [helpArticle, setHelpArticle] = useState<HelpArticle>();
    const [loading, setLoading] = useState(true);
    const [errorKey, setErrorKey] = useState<string>();

    useQuery(helpArticleTemplate, {
        variables: {
            articleId: articleId,
        },
        onCompleted: ({ helpArticle }) => {
            setHelpArticle(helpArticle);
            setErrorKey(undefined);
            setLoading(false);
            document.getElementById('help-article-wrapper')?.scrollIntoView();
        },
        onError(error) {
            setHelpArticle(undefined);
            setErrorKey(error.message.includes('code 404') ? 'ARTICLE_NOT_FOUND' : 'ARTICLE_LOAD_FAILED');
            setLoading(false);
        },
    });

    useEffect(() => {
        const links = document.querySelectorAll<HTMLAnchorElement>('#help-article-wrapper a');
        links.forEach((link) => {
            const helpLink = classifyHelpLink(link.href);
            if (helpLink.kind === 'external') {
                // Not ours to handle: let the browser open it, in a new tab so the app stays.
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                return;
            }
            link.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (helpLink.kind === 'article') {
                    setLoading(true);
                    onLinkClick(helpLink.articleId);
                } else {
                    setErrorKey(undefined);
                    openHelpArticleAttachment(helpLink.path).catch(() => setErrorKey('FILE_OPEN_FAILED'));
                }
            });
        });
    }, [helpArticle]);

    if (loading) {
        return <LinearProgress color="secondary" />;
    }

    return (
        <Fragment>
            <style>{`
            #help-article-wrapper img {
                max-width: 100%;
                border: 1px solid #eee;
            }
            `}</style>
            {errorKey ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {translate(`help-drawer:${errorKey}`)}
                </Alert>
            ) : undefined}
            {helpArticle ? (
                <Fragment>
                    <Typography variant="h2">{helpArticle.title}</Typography>
                    <div id="help-article-wrapper" dangerouslySetInnerHTML={{ __html: helpArticle.body || '' }} />
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default HelpArticleView;
